import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicLayout } from "@/components/PublicLayout";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import api from "@/lib/api";
import { creditTypeLabel } from "@/lib/creditTypes";
import { Check } from "lucide-react";

export function PricingGrid({ onBuy, needType, onClearNeed }) {
  const { t, tl, lang } = useI18n();
  const [allProducts, setProducts] = useState([]);
  useEffect(() => { api.get("/products").then(({ data }) => setProducts(data)); }, []);
  const matching = needType ? allProducts.filter((p) => p.credit_type === needType) : [];
  const products = matching.length ? matching : allProducts;

  const scopeLabel = (scope) => scope?.includes("all") ? "Fitness + Pilates" : scope?.join(", ");

  const CAT = {
    membership: { es: "Suscripciones adulto", en: "Adult subscriptions", fr: "Abonnements adulte" },
    pack: { es: "Bonos de clases", en: "Class packs", fr: "Cartes de cours" },
    private: { es: "Clases privadas", en: "Private classes", fr: "Cours privés" },
    youth: { es: "Teens & Kids", en: "Teens & Kids", fr: "Teens & Kids" },
    pilates_group: { es: "Pilates · Clases grupales", en: "Pilates · Group classes", fr: "Pilates · Cours collectifs" },
    pilates_private: { es: "Pilates · Clases privadas", en: "Pilates · Private classes", fr: "Pilates · Cours privés" },
    trial: { es: "Prueba", en: "Trial", fr: "Essai" },
  };
  const order = ["membership", "pack", "private", "youth", "pilates_group", "pilates_private", "trial"];
  const grouped = order
    .map((cat) => [cat, products.filter((p) => p.category === cat)])
    .filter(([, list]) => list.length > 0);
  const others = products.filter((p) => !order.includes(p.category));
  if (others.length) grouped.push(["other", others]);

  const card = (p, i) => (
    <motion.div key={p.product_id} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.04 }}
      data-testid={`product-card-${p.product_id}`}
      className="flex flex-col rounded-2xl border border-border bg-card p-6 shadow-sm">
      <h3 className="font-display text-lg font-bold leading-tight">{tl(p.name)}</h3>
      {tl(p.description) && <p className="mt-1 text-xs text-muted-foreground">{tl(p.description)}</p>}
      <div className="mt-4 font-display text-3xl font-bold">${p.price}{p.category === "membership" || p.auto_renew ? <span className="text-base font-medium text-muted-foreground">/mo</span> : null}</div>
      <ul className="mt-4 flex-1 space-y-1.5 text-sm">
        <li className="flex items-center gap-2"><Check className="h-4 w-4 shrink-0 text-primary" />{p.credits >= 999 ? "∞" : p.credits} {t("pricing.credits")}</li>
        <li className="flex items-center gap-2"><Check className="h-4 w-4 shrink-0 text-primary" />{p.validity_days} {t("pricing.validity")}</li>
      </ul>
      <Button data-testid={`buy-btn-${p.product_id}`} onClick={() => onBuy(p)} className="mt-5 rounded-full">{t("pricing.buy")}</Button>
    </motion.div>
  );

  return (
    <div className="space-y-12">
      {needType && matching.length > 0 && (
        <div data-testid="need-pack-banner" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-primary/40 bg-primary/10 px-5 py-4">
          <p className="text-sm">
            {tl({ es: `Para reservar esa clase necesitas un pack ${creditTypeLabel(needType, lang)}. Aquí están los packs compatibles:`,
                  en: `To book that class you need a ${creditTypeLabel(needType, lang)} pack. Here are the compatible packs:`,
                  fr: `Pour réserver ce cours, il te faut un pack ${creditTypeLabel(needType, lang)}. Voici les packs compatibles :` })}
          </p>
          <button data-testid="need-pack-show-all" onClick={onClearNeed} className="text-sm font-medium text-primary underline-offset-2 hover:underline">
            {tl({ es: "Ver todos los packs", en: "See all packs", fr: "Voir tous les packs" })}
          </button>
        </div>
      )}
      {grouped.map(([cat, list]) => (
        <div key={cat}>
          <h2 className="mb-5 font-display text-2xl font-bold tracking-tight">{(CAT[cat] || { [lang]: cat })[lang] || cat}</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {list.map((p, i) => card(p, i))}
          </div>
        </div>
      ))}
    </div>
  );
}

const PRICING_CRUMBS = [{ name: "Precios", path: "/pricing" }];

function useOffersLd() {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products").then(({ data }) => setProducts(data || [])).catch(() => {}); }, []);
  return useMemo(() => products.length ? {
    "@type": "OfferCatalog", name: "Packs y membresías Frenchies Fitness & Pilates Coronado", url: "https://frenchiesfitness.com/pricing",
    itemListElement: products.filter((p) => p.active !== false).map((p) => ({
      "@type": "Offer", name: p.name?.es, price: Number(p.price).toFixed(2), priceCurrency: "USD", availability: "https://schema.org/InStock",
      url: "https://frenchiesfitness.com/pricing", itemOffered: { "@type": "Service", name: p.name?.es, provider: { "@type": "LocalBusiness", name: "Frenchies Fitness & Pilates" } },
    })),
  } : null, [products]);
}

export default function Pricing() {
  const offersLd = useOffersLd();
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <PublicLayout>
      <Seo
        title="Precios y Membresías del Gym — Frenchies Fitness & Pilates Coronado"
        description="Precios del gym en Coronado, Panamá: membresías, bonos de clases de fitness, functional movement, y Pilates Reformer, clases privadas y programas para niños. Reserva y paga en línea."
        path="/pricing"
        keywords="precios gym Coronado, membresía gym Coronado, cuánto cuesta gym Coronado, bonos Pilates Coronado, clases privadas Coronado, Fitness Coronado, functional movement Coronado"
        jsonLd={offersLd}
        breadcrumbs={PRICING_CRUMBS}
      />
      <section className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
        <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">{t("pricing.title")}</h1>
        <p className="mb-10 mt-2 text-muted-foreground">{t("pricing.subtitle")}</p>
        <PricingGrid onBuy={() => navigate(user ? "/app/credits" : "/register")} />
      </section>
    </PublicLayout>
  );
}
